import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { setMediaInfo } from "../../../store/business/businessReducer";
import businessService from "../../../store/business/businessService";

const MediaInfo = ({ onNext, onPrevious }: { onNext: () => void; onPrevious: () => void }) => {
    const [uploadProgress, setUploadProgress] = useState<number[]>([]);
    const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
    const token = useSelector((state: any) => state.auth.token); // Assuming auth token is in state
    const dispatch = useDispatch();

    const validationSchema = Yup.object({
        uploadedImages: Yup.array()
            .min(1, "At least one image is required")
            .required("Please upload at least one image"),
    });

    const formik = useFormik({
        initialValues: { uploadedImages: [] as { name: string; url: string }[] },
        validationSchema,
        onSubmit: () => {
            const imageUrls = formik.values.uploadedImages.map((img) => img.url);
            dispatch(setMediaInfo({ uploadedImages: imageUrls })); // Store URLs in Redux
            onNext();
        },
    });

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            "image/jpeg": [],
            "image/png": [],
            "image/gif": [],
        },
        onDrop: async (acceptedFiles) => {
            const newProgress = new Array(acceptedFiles.length).fill(0);
            setUploadProgress(newProgress);

            for (const [index, file] of acceptedFiles.entries()) {
                try {
                    const urls = await businessService.uploadImages([file], token, (progress) => {
                        setUploadProgress((prev) => {
                            const updated = [...prev];
                            updated[index] = progress;
                            return updated;
                        });
                    });

                    const newImageEntries = urls.map((url) => ({
                        name: file.name,
                        url,
                    }));

                    formik.setFieldValue("uploadedImages", [
                        ...formik.values.uploadedImages,
                        ...newImageEntries,
                    ]);

                    setUploadedUrls((prev) => [...prev, ...urls]);
                } catch (error) {
                    console.error("Image upload failed:", error);
                }
            }
        },
    });

    return (
        <form onSubmit={formik.handleSubmit} className="space-y-6">
            <h2 className="text-lg font-bold mb-6">Media Information</h2>
            <div
                {...getRootProps()}
                className={`relative block w-full rounded-lg border-2 border-dashed p-12 text-center ${isDragActive ? "border-green-500" : "border-gray-300 hover:border-gray-400"
                    }`}
            >
                <input {...getInputProps()} />
                <span className="text-gray-900">Drag & drop images or click to upload</span>
            </div>

            <ul className="mt-4">
                {formik.values.uploadedImages.map((file, index) => (
                    <li key={index} className="flex items-center justify-between">
                        <span>{file.name}</span>
                        <progress value={uploadProgress[index] || 0} max="100"></progress>
                        <button
                            type="button"
                            onClick={() => {
                                const updatedFiles = formik.values.uploadedImages.filter((_, i) => i !== index);
                                formik.setFieldValue("uploadedImages", updatedFiles);
                                setUploadedUrls((prev) => prev.filter((_, i) => i !== index));
                            }}
                            className="text-red-500"
                        >
                            Remove
                        </button>
                    </li>
                ))}
            </ul>

            <div className="flex justify-between">
                <button type="button" onClick={onPrevious} className="bg-gray-300 px-4 py-2 rounded">
                    Previous
                </button>
                <button type="submit" className="bg-green-500 px-4 py-2 rounded text-white">
                    Next
                </button>
            </div>
        </form>
    );
};

export default MediaInfo;
